import React, { useCallback } from 'react';

import moment from 'moment';
import Datetime from 'react-datetime';

import { Colors } from 'theme/colors';
import { DateItem } from './styles';

const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Calendar selector component (for web).
 *
 * @param props - Calendar selector props.
 */
export function CalendarSelector(props: Props) {
  const { minDate, maxDate, value, onChange, accentColor } = props;

  // Add state to track selected date
  const [selectedDate, setSelectedDate] = React.useState(value);

  const validate = React.useCallback(
    (current: moment.Moment) => {
      const date = moment(minDate);

      return date.isSameOrBefore(current) && current.isBefore(maxDate);
    },
    [minDate, maxDate]
  );

  const handleOnChange = useCallback(
    (date: string | moment.Moment) => {
      const formattedDate = typeof date === 'string' ? date : date.format(DATE_FORMAT);
      setSelectedDate(formattedDate);
      onChange(formattedDate);
    },
    [onChange]
  );

  const renderDay = useCallback(
    (attrs: DayProps, currentDate: moment.Moment, selectedDate: moment.Moment) => {
      const isSelected = currentDate.isSame(selectedDate);
      const backgroundColor = isSelected ? accentColor : Colors.white;

      return (
        <DateItem as="td" backgroundColor={backgroundColor} {...attrs}>
          {currentDate.date()}
        </DateItem>
      );
    },
    [accentColor]
  );

  return (
    <Datetime
      value={selectedDate}
      input={false}
      isValidDate={validate}
      onChange={handleOnChange}
      timeFormat={false}
      closeOnSelect={false}
      renderDay={renderDay}
      open={true}
      dateFormat={DATE_FORMAT}
    />
  );
}
/* Type definitions */
interface DayProps {
  /** Unique item key */
  key: string;
  /** Item value */
  'data-value': string;
  /** Date time class */
  className: string;
}
interface Props {
  /** Base color of calender */
  accentColor: string;
  /** Maximum selectable date (inclusive) */
  maxDate?: string;
  /** Minimum selectable date  (inclusive) */
  minDate?: string;
  /** Value of the current date on calender (ISO 8601) */
  value: string;
  /**
   * Event handler that fires when a date is selected.
   *
   * @param selectedDate - Selected date in ISO 8601 format.
   */
  onChange(selectedDate: string): void;
}
